import { saveAs } from "file-saver"
import { debounce } from "lodash"
import { applySnapshot, cast, destroy, getSnapshot, types } from "mobx-state-tree"
import { DATE_FORMAT, DATE_TIME_FORMAT, EMPTY_OBJECT_ID } from "modules/common/constants"
import moment from "moment"
import { isNewlyCreated } from "modules/common/models/entity"
import { flow } from "modules/common/models/flow"
import { Notificator } from "modules/common/models/notificator"
import { Queryable } from "modules/common/models/queryable"
import { Transport } from "modules/common/models/transport"
import { apiUrls } from "modules/common/services/communication/urls"
import { base64ToBlob } from "modules/common/services/files"
import { formatDate } from "modules/common/services/formatting/date"
import { trimStart } from "modules/common/services/strings"
import { texts } from "modules/common/texts"
import { OrderDictionary, initialState as emptyOrders } from "modules/orders-manage/models/order-dictionary"
import {
  MessageType,
  OrderQuestion,
  OrderQuestionSnapshotType,
  SaveQuestion,
  initialState as emptyQuestion,
  questionFields,
} from "modules/orders-manage/models/order-question"
import {
  EmployerDictionary,
  initialState as emptyEmployee,
} from "modules/spending/employee/models/employee-dictionary"
import { parse } from "query-string"
import {
  BoardColumn,
  BoardColumnSnapshotType,
  DEFAULT_TAG_COLOR,
  Portfolio,
  QuestionBoardSnapshot,
  QuestionTag,
  SaveColumn,
  SystemColumns,
} from "./board"
import { ColumnPage, QuestionCardType, QuestionCardsPage, QuestionChild } from "./card"

export const OrderQuestionStore = types
  .compose(
    Notificator,
    Transport,
    Queryable,
    types.model({
      /** Загруженные страницы колонок: ключ колонки → её карточки */
      pages: types.map(ColumnPage),
      question: OrderQuestion,
      employee: EmployerDictionary,
      orders: OrderDictionary,
      columns: types.array(BoardColumn),
      portfolios: types.array(Portfolio),
      /** Справочник меток открытой доски: у каждой доски набор свой */
      tags: types.array(QuestionTag),
      /** Открытая доска: пусто — задания по проектам, иначе личная доска */
      portfolioId: types.string,
      boardEnabled: types.boolean,
      customColumnsEnabled: types.boolean,
      portfoliosEnabled: types.boolean,
      /** Текущий пользователь может завести свою доску: приглашённым это не нужно */
      canCreatePortfolio: types.boolean,
      canManageColumns: types.boolean,
      loading: types.boolean,
      /** Первая загрузка доски и переключение доски: показываем заглушку вместо колонок */
      boardLoading: types.boolean,
      /** Фильтры по людям: каждого можно выбрать несколько, как и объектов */
      filtersEmployerIds: types.array(types.string),
      filtersImplementerIds: types.array(types.string),
      filtersAuthorIds: types.array(types.string),
      /** Фильтр по объектам: в шапке их можно выбрать несколько */
      filtersOrderIds: types.array(types.string),
      /** Договор, во вкладке которого открыта доска: фильтр по объекту там не показан */
      orderContext: types.string,
      filtersByType: types.string,
      filtersByEmpId: types.string,
      /**
       * Задание из ссылки: доска не фильтруется по нему, но его колонка приходит сразу
       * до нужной карточки — иначе она осталась бы за пределами первой страницы
       */
      revealId: types.string,
    })
  )
  .views((self) => ({
    /**
     * Единственный выбранный объект: вкладка договора работает с одним, и по нему же
     * подставляется проект в новое задание. Когда объектов несколько — пусто
     */
    get filtersOrderId() {
      return self.filtersOrderIds.length === 1 ? self.filtersOrderIds[0] : ""
    },
  }))
  .views((self) => ({
    /** Отбор карточек целиком на сервере: клиенту приходит только страница колонки */
    get queryParams() {
      const filters: TStringMap<any> = {}

      if (self.filtersOrderIds.length > 0) {
        filters.orderIds = self.filtersOrderIds.slice()
      }

      if (self.revealId) {
        filters.ensureQuestionId = self.revealId
      }

      if (self.filtersByType) {
        filters.typeText = self.filtersByType
      }

      if (self.filtersByEmpId) {
        filters.employerId = self.filtersByEmpId
      }

      if (self.portfolioId) {
        filters.portfolioId = self.portfolioId
      }

      if (self.filtersAuthorIds.length > 0) {
        filters.authorIds = self.filtersAuthorIds.slice()
      }

      if (self.filtersEmployerIds.length > 0) {
        filters.filterEmployerIds = self.filtersEmployerIds.slice()
      }

      if (self.filtersImplementerIds.length > 0) {
        filters.filterImplementerIds = self.filtersImplementerIds.slice()
      }

      if (self.pureQuery) {
        filters.complexSearch = self.pureQuery
      }

      return filters
    },
  }))
  .views((self) => ({
    /** Загруженные карточки колонки по её ключу */
    page(columnKey: string) {
      return self.pages.get(columnKey)
    },

    /** Сколько карточек показано на доске */
    get cardsCount() {
      let result = 0

      self.pages.forEach((page) => (result += page.items.length))

      return result
    },

    /** Карточка по идентификатору в любой из колонок */
    findCard(id: string): QuestionCardType | null {
      let result: QuestionCardType | null = null

      self.pages.forEach((page) => {
        result = result || page.items.find((item) => item.id === id) || null
      })

      return result
    },

    /** Цвета меток из справочника: название → цвет текста на карточке */
    get tagColors(): TStringMap<string> {
      return self.tags.reduce((acc, tag) => {
        acc[tag.name] = tag.color
        return acc
      }, {} as TStringMap<string>)
    },

    get currentPortfolio() {
      return self.portfolios.find((p) => p.id === self.portfolioId) || null
    },

    /**
     * Показывать ли шапку с досками. Личные доски могут быть включены в настройках,
     * но сотруднику, который не вправе их заводить и никуда не приглашён, показывать
     * нечего — вкладка «Задания по проектам» осталась бы единственной.
     */
    get portfolioBarVisible() {
      return self.portfoliosEnabled && (self.canCreatePortfolio || self.portfolios.length > 0)
    },

    /** Участники открытой личной доски: владелец и приглашённые. Пусто — доска по проектам */
    get portfolioMembers(): { id: string; name: string }[] | null {
      const portfolio = self.portfolios.find((p) => p.id === self.portfolioId)

      if (!portfolio) {
        return null
      }

      const result = portfolio.owner ? [{ id: portfolio.owner.id, name: portfolio.owner.name }] : []

      portfolio.members.forEach((member) => {
        result.some((p) => p.id === member.id) || result.push({ id: member.id, name: member.name })
      })

      return result
    },
  }))
  .actions((self) => {
    const requestCards = (columnKey?: string, skip?: number) =>
      self.transport.post<QuestionCardsPage>(apiUrls.orders.orderQuestion.cards, {
        ...self.queryParams,
        columnKey: columnKey || "",
        skip: skip || 0,
      })

    return {
      /** Колонки, доски и флаги настроек */
      loadBoard: flow(function* () {
        try {
          const board: QuestionBoardSnapshot = yield self.transport.post<QuestionBoardSnapshot>(
            apiUrls.orders.orderQuestion.board,
            // во вкладке договора сервер оставит только доски, где у пользователя
            // есть задание по этому договору
            { portfolioId: self.portfolioId, orderId: self.orderContext }
          )

          applySnapshot(self.columns, board.columns)
          applySnapshot(self.portfolios, board.portfolios)
          applySnapshot(self.tags, board.tags ?? [])
          self.boardEnabled = board.boardEnabled
          self.customColumnsEnabled = board.customColumnsEnabled
          self.portfoliosEnabled = board.portfoliosEnabled
          self.canCreatePortfolio = board.canCreatePortfolio
          self.canManageColumns = board.canManageColumns

          // доску могли удалить на другом устройстве
          if (self.portfolioId && !board.portfolios.some((p) => p.id === self.portfolioId)) {
            self.portfolioId = ""
          }

          return true
        } catch (e) {
          self.notify.error(e)
          return false
        }
      }),

      /** Первые страницы всех колонок */
      load: flow(function* () {
        try {
          // во вкладке договора фильтр по объекту скрыт, справочник там не нужен
          if (!self.orderContext) {
            self.orders.isEmpty && self.orders.load()
          }

          if (self.employee.isEmpty) {
            yield self.employee.load()
          }

          if (self.filtersOrderId === EMPTY_OBJECT_ID) {
            self.pages.clear()
            return true
          }

          const page: any = yield requestCards()

          const known: string[] = []

          ;(page as QuestionCardsPage).columns.forEach((column) => {
            known.push(column.columnKey)

            self.pages.set(column.columnKey, {
              columnKey: column.columnKey,
              total: column.total,
              items: column.items,
              loading: false,
            })
          })

          // колонку могли удалить, пока доска была открыта
          Array.from(self.pages.keys()).forEach((key) => {
            known.indexOf(key) < 0 && self.pages.delete(key)
          })

          return true
        } catch (e) {
          self.notify.error(e)
          return false
        }
      }),

      /** Следующая страница одной колонки: прокрутка или кнопка «Показать ещё» */
      loadMore: flow(function* (columnKey: string) {
        const page = self.pages.get(columnKey)

        if (!page || page.loading || !page.hasMore) {
          return false
        }

        page.loading = true

        try {
          const loaded: any = yield requestCards(columnKey, page.items.length)
          const next = (loaded as QuestionCardsPage).columns.find((c) => c.columnKey === columnKey)

          if (next) {
            page.total = next.total

            // карточку могли перенести между запросами — не показываем её дважды
            next.items.forEach((item) => {
              page.items.some((known) => known.id === item.id) || page.items.push(item)
            })
          }

          return true
        } catch (e) {
          self.notify.error(e)
          return false
        } finally {
          page.loading = false
        }
      }),

      uploadFile: flow(function* (file: File) {
        try {
          const model = new FormData()

          model.append("file", file)
          model.append("accept", "*")

          const result: UploadFileResult = yield self.transport.post<any>(apiUrls.application.files.upload, model)
          const { id, mimeType, previewMimeType } = result

          const fileBase: FileBase = { fileId: id, fileName: file.name, previewMimeType, mimeType }
          return fileBase
        } catch (er) {
          self.notify.error(er)
          return null
        }
      }),
    }
  })
  .actions((self) => ({
    setLoading(value: boolean) {
      self.loading = value
    },

    setBoardLoading(value: boolean) {
      self.boardLoading = value
    },

    /** Задание из ссылки: его колонка догружается до карточки, пока ссылку не закрыли */
    setRevealId(id: string) {
      self.revealId = id
    },

    /** Фильтры открытой доски: у другой доски они свои, поэтому чужие не тянем */
    applyStoredFilters() {
      const saved = readBoardFilters()[boardFiltersKey(self.portfolioId, self.orderContext)]

      self.filtersEmployerIds = cast(storedIds(saved?.employer))
      self.filtersImplementerIds = cast(storedIds(saved?.implementer))
      self.filtersAuthorIds = cast(storedIds(saved?.author))

      // во вкладке договора объект задан самим договором, сохранённый фильтр там не при чём
      if (!self.orderContext) {
        self.filtersOrderIds = cast(saved?.orders ?? [])
      }
    },

    /** Запоминаем фильтры доски: при возврате к ней и после перезагрузки они восстановятся */
    storeFilters() {
      const all = readBoardFilters()
      const key = boardFiltersKey(self.portfolioId, self.orderContext)

      all[key] = {
        employer: self.filtersEmployerIds.slice(),
        implementer: self.filtersImplementerIds.slice(),
        author: self.filtersAuthorIds.slice(),
        // во вкладке договора объект задан самим договором: выбор пользователя не затираем
        orders: self.orderContext ? all[key]?.orders || [] : self.filtersOrderIds.slice(),
      }

      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(all))
    },

    /**
     * Доска, на которой лежит задание. Ссылка из уведомления ведёт на конкретное
     * задание, а выдача карточек фильтруется по доске — не переключившись на неё,
     * доска покажет пустоту вместо задания.
     */
    questionPortfolio: flow(function* (id: string) {
      try {
        const data: any = yield self.transport.get<any>(apiUrls.orders.orderQuestion.details(id))

        return (data && data.portfolioId) || ""
      } catch (er) {
        // задание недоступно или запрос не прошёл — доску не трогаем
        return null
      }
    }),

    getById: flow(function* (id: string) {
      try {
        const data: OrderQuestionSnapshotType = yield self.transport.get<any>(
          apiUrls.orders.orderQuestion.details(id)
        )
        applySnapshot(self.question, data)

        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    save: flow(function* (message: SaveQuestion) {
      try {
        const body: TStringMap<any> = { ...message }

        if (body[questionFields.stopDate] instanceof Date) {
          body[questionFields.stopDate] = formatDate(body[questionFields.stopDate], DATE_TIME_FORMAT)
        }

        if (Array.isArray(body[questionFields.questionFiles])) {
          body[questionFields.questionFiles] = body[questionFields.questionFiles].map((doc: FileBase) => doc.fileId)
        }

        if (Array.isArray(body[questionFields.answerFiles])) {
          body[questionFields.answerFiles] = body[questionFields.answerFiles].map((doc: FileBase) => doc.fileId)
        }

        if (!body[questionFields.orderId]) {
          body[questionFields.orderId] = EMPTY_OBJECT_ID
        }

        // На доске по проектам срок обязателен: при быстром добавлении ставим неделю.
        // На личной доске задание может жить вообще без срока — отправляем пустой
        if (!body[questionFields.stopDate]) {
          body[questionFields.stopDate] = body[questionFields.portfolioId] ? null : DEFAULT_STOP_DATE()
        }

        if (!body[questionFields.parent]) {
          body[questionFields.parent] = ""
        }

        body[questionFields.parentQuestion] = null

        isNewlyCreated(message.id)
          ? yield self.transport.put<any>(apiUrls.orders.orderQuestion.create, body)
          : yield self.transport.post<any>(apiUrls.orders.orderQuestion.update(message.id), body)

        // в задании могли завести или перекрасить метку — обновляем справочник на месте
        message.tags.forEach((tag) => {
          // цвет приходит только у меток, которые в этом окне завели или перекрасили
          const picked = message.tagColors[tag]
          const known = self.tags.find((t) => t.name === tag)

          if (known) {
            // у знакомой метки цвет свой: без выбранного цвета он остаётся прежним
            picked && (known.color = picked)
            return
          }

          self.tags.push({ id: "", name: tag, color: picked || DEFAULT_TAG_COLOR })
        })

        yield self.load()

        self.notify.success(texts.messages.saved)
        return true
      } catch (e) {
        self.notify.error(e)
        return false
      }
    }),

    /**
     * Перенос карточки. Порядок пересчитываем ровно так же, как сервер — по соседу сверху,
     * поэтому доска встаёт на место сразу и после ответа не перескакивает
     */
    move: flow(function* (questionId: string, columnId: string, sortOrder: number, afterId: string) {
      const from = self.columns
        .map((c) => self.pages.get(c.key))
        .find((page) => !!page && page.items.some((q) => q.id === questionId))

      const card = from?.items.find((q) => q.id === questionId)

      if (!from || !card) {
        return false
      }

      // «Просрочено» сервер расставляет сам по сроку — повторяем это правило сразу,
      // иначе карточка вернётся в другую колонку при следующей загрузке
      const expired =
        columnId === SystemColumns.inWork &&
        !!card.stopDate &&
        moment(card.stopDate, DATE_FORMAT).isBefore(moment().startOf("day"))

      const columnKey = expired ? SystemColumns.expired : columnId
      const to = self.pages.get(columnKey)

      if (!to) {
        return false
      }

      // на проверку задание уходит выполненным, а возврат в работу отметки снимает
      const ready =
        columnKey === SystemColumns.review || columnKey === SystemColumns.done
          ? card.executorsCount
          : columnKey === SystemColumns.inWork || columnKey === SystemColumns.expired
          ? 0
          : card.readyCount

      const moved = {
        ...getSnapshot(card),
        columnId: columnKey,
        completed: columnId === SystemColumns.done,
        readyCount: ready,
      }

      // сначала убираем карточку с прежнего места, иначе позиция соседа сверху съедет
      destroy(card)

      if (from !== to) {
        from.total = Math.max(0, from.total - 1)
        to.total = to.total + 1
      }

      const after = afterId ? to.items.findIndex((q) => q.id === afterId) : -1
      const position = after >= 0 ? after + 1 : Math.max(0, Math.min(sortOrder, to.items.length))

      to.items.splice(position, 0, moved as any)

      try {
        yield self.transport.post<any>(apiUrls.orders.orderQuestion.move(questionId), {
          columnId,
          sortOrder,
          afterId,
        })

        return true
      } catch (e) {
        // сервер отказал — возвращаем доску в прежний вид
        self.notify.error(e)
        yield self.load()

        return false
      }
    }),

    /**
     * Исполнитель отмечает, что закончил работу: когда отметились все — задание уходит на проверку.
     * Ответственный, автор и сотрудник с полным доступом отмечают за исполнителя — тогда указан employerId
     */
    setReadiness: flow(function* (questionId: string, ready: boolean, employerId?: string, all?: boolean) {
      try {
        const saved: OrderQuestionSnapshotType = yield self.transport.post<any>(
          apiUrls.orders.orderQuestion.readiness(questionId),
          { ready, employerId: employerId ?? "", all: !!all }
        )

        if (saved) {
          applySnapshot(self.question, saved)
        }

        // карточка могла уехать на проверку — перезагружаем страницы колонок
        yield self.load()

        return true
      } catch (e) {
        self.notify.error(e)
        return false
      }
    }),

    /**
     * Перенос задания на другую доску. Колонку выбирают в окне переноса,
     * подзадания уезжают вместе с заданием только по согласию
     */
    moveToBoard: flow(function* (
      questionId: string,
      boardId: string,
      columnId: string,
      withChildren: boolean
    ) {
      try {
        yield self.transport.post<any>(apiUrls.orders.orderQuestion.moveToBoard(questionId), {
          boardId,
          columnId,
          withChildren,
        })

        yield self.load()

        self.notify.success(texts.messages.saved)
        return true
      } catch (e) {
        self.notify.error(e)
        return false
      }
    }),

    /** Подзадания задания: список нужен окну переноса, поэтому грузим по требованию */
    questionChildren: flow(function* (questionId: string) {
      try {
        const children: QuestionChild[] = yield self.transport.post<any>(
          apiUrls.orders.orderQuestion.children(questionId)
        )

        return children || []
      } catch (e) {
        self.notify.error(e)
        return []
      }
    }),

    /** Колонки другой доски: грузятся, когда её выбрали в окне переноса */
    loadBoardColumns: flow(function* (boardId: string) {
      try {
        const columns: BoardColumnSnapshotType[] = yield self.transport.post<any>(
          apiUrls.orders.orderQuestion.boardColumns,
          { boardId }
        )

        return columns || []
      } catch (e) {
        self.notify.error(e)
        return []
      }
    }),

    saveMessage: flow(function* (questionId: string, message: MessageType) {
      try {
        const res = yield self.transport.post<MessageType[]>(apiUrls.orders.orderQuestion.saveMessage(questionId), {
          message,
        })

        if (res) {
          applySnapshot(self.question.messages, res)

          // на карточке комментарии показаны числом
          self.pages.forEach((page) => {
            const card = page.items.find((q) => q.id === questionId)
            card && (card.messagesCount = res.length)
          })
        }

        return res
      } catch (e) {
        self.notify.error(e)
        return []
      }
    }),

    deleteMessage: flow(function* (questionId: string, messageId: string) {
      try {
        const res = yield self.transport.post<MessageType[]>(
          apiUrls.orders.orderQuestion.deleteMessage(questionId),
          { messageId }
        )

        if (res) {
          if (self.question && self.question.id === questionId) {
            self.question = cast({ ...self.question, messages: res })
          }

          self.pages.forEach((page) => {
            const card = page.items.find((q) => q.id === questionId)
            card && (card.messagesCount = res.length)
          })
        }

        return res
      } catch (e) {
        self.notify.error(e)
        return []
      }
    }),

    remove: flow(function* (questions: Identified[]) {
      try {
        const ids = questions.map((m) => m.id)

        yield self.transport.delete<any>(apiUrls.orders.orderQuestion.remove, { data: { ids } })
        yield self.load()

        self.notify.success(texts.messages.removed)
        return true
      } catch (e) {
        self.notify.error(e)
        return false
      }
    }),

    printQuestions: flow(function* () {
      try {
        // печать берёт все задания отбора, а не только показанные на доске
        const questions: OrderQuestionSnapshotType[] = yield self.transport.post<any>(
          apiUrls.orders.orderQuestion.list,
          self.queryParams
        )

        const file: FileDescription = yield self.transport.post<any>(apiUrls.orders.orderQuestion.print, {
          questions,
        })

        const blob: any = yield base64ToBlob(file.content || "", file.mimeType)
        saveAs(blob, file.name)

        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),
  }))
  .actions((self) => ({
    /** Сохранение набора колонок доски целиком: имя, цвет, порядок, архив, добавление и удаление */
    saveColumns: flow(function* (columns: SaveColumn[]) {
      try {
        const saved = yield self.transport.post<any>(apiUrls.orders.orderQuestion.columns, {
          portfolioId: self.portfolioId,
          columns,
        })

        applySnapshot(self.columns, saved)
        yield self.load()

        self.notify.success(texts.messages.saved)
        return true
      } catch (e) {
        self.notify.error(e)
        return false
      }
    }),

    savePortfolio: flow(function* (portfolio: { id: string; name: string; color: string; memberIds: string[] }) {
      try {
        yield self.transport.post<any>(apiUrls.orders.orderQuestion.portfolio, portfolio)
        yield self.loadBoard()

        self.notify.success(texts.messages.saved)
        return true
      } catch (e) {
        self.notify.error(e)
        return false
      }
    }),

    removePortfolio: flow(function* (id: string) {
      try {
        yield self.transport.delete<any>(apiUrls.orders.orderQuestion.removePortfolio(id))

        if (self.portfolioId === id) {
          self.portfolioId = ""
          self.applyStoredFilters()
        }

        yield self.loadBoard()
        yield self.load()

        self.notify.success(texts.messages.removed)
        return true
      } catch (e) {
        self.notify.error(e)
        return false
      }
    }),

    selectPortfolio: flow(function* (portfolioId: string) {
      self.portfolioId = portfolioId

      // во вкладке договора доску не запоминаем: это выбор для конкретного договора,
      // страница трекера должна открыться на своей прежней доске
      if (!self.orderContext) {
        localStorage.setItem(PORTFOLIO_STORAGE_KEY, portfolioId)
      }

      // у каждой доски свои фильтры: чужие сбрасываем, её собственные восстанавливаем
      self.applyStoredFilters()

      // индикацию мог включить вызывающий код — тогда снимать её не нам
      const owned = !self.boardLoading
      owned && self.setBoardLoading(true)

      // карточки прежней доски убираем сразу: иначе они мелькнут в её колонках
      self.pages.clear()

      try {
        yield self.loadBoard()
        yield self.load()
      } finally {
        owned && self.setBoardLoading(false)
      }

      return true
    }),
  }))
  .actions((self) => ({
    /** Новая колонка встаёт последней справа */
    addColumn(name: string, color: string) {
      const columns: SaveColumn[] = self.columns.map((c) => ({
        id: c.isSystem ? "" : c.id,
        systemKey: c.systemKey,
        name: c.name,
        color: c.color,
        isArchive: c.isArchive,
      }))

      columns.push({ id: "", systemKey: "", name, color, isArchive: false })

      return self.saveColumns(columns)
    },

    initFilters() {
      self.applyStoredFilters()
    },

    /**
     * Договор вкладки нужно знать до загрузки доски: от него зависит и ключ
     * сохранённых фильтров, и список личных досок, который вернёт сервер
     */
    setOrderContext(orderId?: string) {
      self.orderContext = orderId || ""
    },

    /** Фильтры шапки по людям: каждого можно выбрать несколько */
    setEmployerIds(ids: string[]) {
      self.filtersEmployerIds = cast(ids)
      self.storeFilters()
      self.load()
    },

    setImplementerIds(ids: string[]) {
      self.filtersImplementerIds = cast(ids)
      self.storeFilters()
      self.load()
    },

    setAuthorIds(ids: string[]) {
      self.filtersAuthorIds = cast(ids)
      self.storeFilters()
      self.load()
    },

    /** Фильтр шапки по объектам: их может быть выбрано несколько */
    setOrderIds(ids: string[]) {
      self.filtersOrderIds = cast(ids)
      self.storeFilters()
      self.load()
    },

    setOrderId: flow(function* (orderId?: string, empId?: string, typeText?: string, isMounted?: boolean) {
      const query = ExternalQuestionNumber()

      // вкладка договора всегда показывает его задания, на своей странице объект выбирает пользователь
      self.orderContext = orderId || ""

      if (isMounted) {
        self.filtersByType = ""
        self.filtersByEmpId = ""
      }

      if (orderId || orderId === "" || orderId === undefined) {
        // вкладка договора показывает его один, на своей странице возвращаем выбор пользователя
        if (orderId) {
          self.filtersOrderIds = cast([orderId])
        } else if (orderId === "") {
          self.filtersOrderIds = cast([])
        } else {
          self.filtersOrderIds = cast(storedOrderIds(self.portfolioId))
        }

        self.filtersByType = ""
        self.filtersByEmpId = ""
        self.pureQuery = query.toLowerCase()
        self.query = query
      }

      if (empId || empId === "") {
        self.filtersByEmpId = empId

        // ссылка на задания сотрудника: фильтр по объекту сузил бы их до одного проекта
        if (empId && !self.orderContext) {
          self.filtersOrderIds = cast([])
        }
      }

      if (typeText || typeText === "") {
        self.filtersByType = typeText
      }

      yield self.load()

      return true
    }),

    /**
     * Поиск идёт на сервере: доска перезагружает первые страницы колонок.
     * Ждём паузы в наборе, чтобы не слать запрос на каждую букву
     */
    onQueryChanged: debounce(() => self.load(), 400),

    emptyQuestion(columnId?: string): OrderQuestionSnapshotType {
      const first = self.columns.length > 0 ? self.columns[0].key : SystemColumns.inWork

      // на личной доске срок необязателен, на доске по проектам подставляем неделю
      const stopDate = self.portfolioId ? "" : DEFAULT_STOP_DATE()

      return {
        ...emptyQuestion(),
        stopDate,
        stopDay: stopDate,
        columnId: columnId || first,
        portfolioId: self.portfolioId,
        order: self.filtersOrderId
          ? { id: self.filtersOrderId, inventoryNumber: "", name: "" }
          : null,
        orderId: self.filtersOrderId ? self.filtersOrderId : "",
      }
    },
  }))
  .named("OrderQuestionStore")

export type OrderQuestionStoreType = typeof OrderQuestionStore.Type
export type OrderQuestionStoreSnapshotType = typeof OrderQuestionStore.SnapshotType

const PORTFOLIO_STORAGE_KEY = "questionsPortfolio"

const FILTERS_STORAGE_KEY = "questionsFilters"

/**
 * Фильтры у каждой доски свои: ключ проектной доски — "orders", а личная доска,
 * открытая во вкладке договора, хранит их отдельно от той же доски на трекере
 */
interface BoardFilters {
  /** Выбранные люди и объекты: каждого фильтра может быть несколько */
  employer: string[] | string
  implementer: string[] | string
  author: string[] | string
  orders?: string[]
}

/** Прежние версии хранили в фильтрах по людям одно значение строкой */
const storedIds = (saved: string[] | string | undefined): string[] =>
  Array.isArray(saved) ? saved : saved ? [saved] : []

const boardFiltersKey = (portfolioId: string, orderContext = "") =>
  portfolioId ? (orderContext ? `${orderContext}:${portfolioId}` : portfolioId) : "orders"

const readBoardFilters = (): TStringMap<BoardFilters> => {
  try {
    return JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY) || "{}")
  } catch {
    return {}
  }
}

/** Сохранённые объекты фильтра: доска возвращается к ним и после ухода во вкладку договора */
const storedOrderIds = (portfolioId: string): string[] =>
  readBoardFilters()[boardFiltersKey(portfolioId)]?.orders ?? []

/** Срок исполнения по умолчанию — неделя от сегодняшнего дня */
const DEFAULT_STOP_DATE = () => moment().add(7, "days").format(DATE_FORMAT)

export const initialState = (searchQuery = ""): OrderQuestionStoreSnapshotType => ({
  question: emptyQuestion(),
  pages: {},
  orders: emptyOrders(),
  employee: emptyEmployee(),
  columns: [],
  portfolios: [],
  tags: [],
  portfolioId: "",
  boardEnabled: true,
  customColumnsEnabled: false,
  portfoliosEnabled: false,
  canCreatePortfolio: false,
  canManageColumns: false,
  loading: false,
  boardLoading: true,
  filtersOrderIds: [],
  orderContext: "",
  filtersByType: "",
  filtersByEmpId: "",
  revealId: "",
  filtersEmployerIds: [],
  filtersImplementerIds: [],
  filtersAuthorIds: [],
  query: searchQuery,
  pureQuery: searchQuery.toLowerCase(),
})

export const ExternalQuestionNumber = () => {
  const hash = trimStart("#", window.location.hash)
  const params = parse(hash)

  return (params["number"] as string) ?? ""
}

/** Доска, открытая в прошлый раз */
export const StoredPortfolioId = () => localStorage.getItem(PORTFOLIO_STORAGE_KEY) || ""
